import { useMemo, useRef, useState } from 'react'
import useClickOutsideAndEscape from 'hooks/useClickOutsideAndEscape'
import { CheckBoxGroup } from '..'
import DropdownButton from './dropdownButton'
import { MultiselectDropdownInput } from 'types/input'
import { FieldValues } from 'react-hook-form'

/**
 *
 * Multi Select Dropdown element
 * -
 * Need To Pass Following Params
 * - `name` _required_
 * - `options` _required_
 * - `setValue` _required_
 * - `error` _optional_
 * - `parentClassName` _optional_
 * - `className` _Optional_
 * - `hintText` _optional_
 * - `label` _Optional_
 * - `defaultSelected` _Optional_
 * - `includeSelectAll` _Optional_
 *
 */
const MultiSelectDropdown = <T extends FieldValues>({
  parentClassName = '',
  className = '',
  label,
  name,
  setValue,
  error,
  hintText,
  options,
  defaultSelected,
  includeSelectAll,
  required,
  reset,
}: MultiselectDropdownInput<T>) => {
  // To toggle the dropdown visibility
  const [isVisible, setIsVisible] = useState(false)

  // To store total selected values
  const [totalSelected, setTotalSelected] = useState(0)

  // To close the dropdown in outside click
  const ref = useRef<HTMLDivElement | null>(null)
  useClickOutsideAndEscape({ ref, callback: () => setIsVisible(false) })

  //To handle rendering of Button or Checkbox in the dropdown
  const renderInput = useMemo(() => {
    return (
      <CheckBoxGroup
        setValue={setValue}
        options={options}
        name={name}
        setTotalSelected={setTotalSelected}
        includeSelectAll={includeSelectAll}
        parentClassName="px-2 max-w-[267px] w-full"
        defaultSelected={defaultSelected}
        reset={reset}
      />
    )
  }, [options, reset])

  return (
    <div className={`relative w-full  outline-none ${parentClassName}`}>
      {/* Label */}
      <label
        htmlFor={name}
        className="text-xs font-semibold text-sidebar-primary"
      >
        {label}
        {required && <span className="text-error">*</span>}
      </label>

      {/* Select Button */}
      <div ref={ref}>
        <DropdownButton
          error={error}
          className={className}
          setIsVisible={setIsVisible}
          isVisible={isVisible}
        >
          {`${totalSelected} Selected`}{' '}
        </DropdownButton>

        {/* Options */}
        <div
          className={`absolute z-10 h-max max-h-64 w-full min-w-max origin-top overflow-auto rounded-md bg-white p-2 text-sm shadow-lg transition-all duration-200 ${
            isVisible ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
          }`}
        >
          {renderInput}
        </div>
      </div>

      {/* Error & Hint Text */}
      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 px-3 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
